/**
 * AK-446 Ш2 — «Списать»: акт списания культуры из карточки поля.
 *
 * Почему это документ, а не смена статуса
 * --------------------------------------
 * Списание уничтожает ожидаемый урожай: его потом разбирают в отчёте по
 * гибели, в плане следующего сезона и (бывает) в страховом споре. Поэтому
 * ADR-059 §D8 знает ровно один способ «убить» занятие — **проведённый акт** с
 * причиной и автором; «Погиб» как статус из словаря убран. API этого акта
 * (`crop_writeoff_acts`, ADR-055 §D4) жил с весны, но во фронте не было ни
 * одного обращения — статус «Списано» был недостижим рукой.
 *
 * Два шага документа, а не одна кнопка
 * ------------------------------------
 * `draft` → `confirmed`. Черновик ни на что не влияет; занятие становится
 * «Списано» ТОЛЬКО в момент проведения. Мы показываем это прямо в диалоге —
 * агроном видит, что именно сейчас произойдёт, и может уйти на середине, не
 * оставив поле в непонятном состоянии. Проведённый акт не переписывается: его
 * отменяют (`cancel`) и заводят новый — так требует учётная природа документа.
 *
 * Чего здесь пока НЕТ (осознанно, Ш3)
 * ----------------------------------
 * · **Осмотра с фото** (экран Е-1 wireframe) — механика осмотров существует
 *   отдельно, а модель осмотра гибели сейчас переделывает вертикаль operations
 *   (ADR-056 §D6). Акт умеет ссылаться на осмотр (`inspection_id`), поле
 *   останется — подключим, когда их спека устоится. До тех пор акт создаётся
 *   «ретро» (`inspection_id = NULL`) — контракт это разрешает явно.
 * · **Исхода «Пересеять / Запахать / Оставить»** (экран Е-2) — это не часть
 *   статуса, а подсказка следующего шага; заводить её до того, как есть куда
 *   вести, значит рисовать кнопку в никуда.
 * · **Частичного списания контуром** — акт несёт свой контур (ADR-059 §D10),
 *   UI контура поедет вместе с редактором зон. Площадь можно указать цифрой.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  draftWriteoffAct,
  useConfirmWriteoffAct,
  useCreateWriteoffAct,
  useWriteoffActs,
  useWriteoffReasons,
  writeoffReasonLabel,
} from '@/lib/sowings/writeoffApi';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sowingId: string;
  /** «Пшеница озимая · Круг (орошение)» — что именно списываем. */
  title: string;
}

export function WriteoffActDialog({
  open,
  onOpenChange,
  sowingId,
  title,
}: Props) {
  const reasons = useWriteoffReasons(open);
  const acts = useWriteoffActs(open ? sowingId : null);
  const create = useCreateWriteoffAct(sowingId);
  const confirm = useConfirmWriteoffAct(sowingId);

  const [reasonId, setReasonId] = useState<string>('');
  const [areaHa, setAreaHa] = useState<string>('');
  const [comment, setComment] = useState<string>('');

  const busy = create.isPending || confirm.isPending;
  const failed = create.isError || confirm.isError;

  const submit = async () => {
    if (!reasonId) return;
    // Черновик уже есть (агроном ушёл на середине прошлый раз) — продолжаем
    // его, а не плодим второй документ на то же занятие.
    const existing = draftWriteoffAct(acts.data);
    const act =
      existing ??
      (await create.mutateAsync({
        sowing_id: sowingId,
        reason_id: reasonId,
        affected_area_ha: areaHa ? Number(areaHa.replace(',', '.')) : null,
        comment: comment.trim() || null,
      }));
    await confirm.mutateAsync({ actId: act.id });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Списать посев · {title}</DialogTitle>
          <DialogDescription>
            Списание — документ, а не отметка: причина и автор остаются в акте.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="writeoff-reason">Причина гибели</Label>
            <Select value={reasonId} onValueChange={setReasonId}>
              <SelectTrigger id="writeoff-reason">
                <SelectValue placeholder="Выберите причину" />
              </SelectTrigger>
              <SelectContent>
                {(reasons.data ?? []).map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {writeoffReasonLabel(r)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Обязательна: по причинам потом строится отчёт по гибели.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="writeoff-area">Площадь списания, га</Label>
            <Input
              id="writeoff-area"
              inputMode="decimal"
              placeholder="всё занятие"
              value={areaHa}
              onChange={(e) => setAreaHa(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Пусто — списываем занятие целиком.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="writeoff-comment">Комментарий</Label>
            <Input
              id="writeoff-comment"
              placeholder="Например: вымерзание на низине"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>

          <div className="rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
            <b className="text-foreground">После проведения: </b>
            посев станет «Списано» — урожая по нему не будет. Ошиблись — акт
            можно отменить, статус вернётся к фактическому.
          </div>

          {failed && (
            <p className="text-xs text-destructive">
              Не удалось провести акт. Проверьте, нет ли уже проведённого акта
              по этому посеву.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Отмена
          </Button>
          <Button onClick={() => void submit()} disabled={!reasonId || busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Провести акт
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
